<template>
  <div>
    <!-- ── Floating Banner ─────────────────────────────── -->
    <Transition name="card">
      <div
        v-if="isReady && bannerOpen"
        class="cb-card"
        role="region"
        aria-label="Cookie preferences"
      >
        <button class="cb-dismiss" aria-label="Dismiss" @click="rejectAll">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>

        <div class="cb-body">
          <p class="cb-text">{{ $t("cookies") }}</p>

          <div class="cb-actions">
            <button class="cb-btn cb-btn--primary" @click="acceptAll">
              {{ $t("accept") }}
            </button>
            <button class="cb-btn cb-btn--text" @click="openCustomize">
              {{ $t("customize") }}
            </button>
            <button class="cb-btn cb-btn--ghost" @click="rejectAll">
              {{ $t("reject") }}
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- ── Persistent cookie circle: shown only after a reject (× or Reject),
         until the visitor accepts. Toggles the banner. ── -->
    <Transition name="circle">
      <button
        v-if="isReady && pendingReject"
        class="cb-circle"
        type="button"
        :aria-label="$t('cookiePreference')"
        :title="$t('cookiePreference')"
        @click="toggleBanner"
      >
        <svg
          width="30"
          height="30"
          viewBox="0 0 64 64"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M52.5 31.5C50.2 32.2 47.5 32.3 45.1 31.4C41.4 30 39.2 26.3 40.1 22.5C36.4 23.4 32.7 21.2 31.3 17.5C30.4 15.1 30.5 12.4 31.2 10.1C29.8 9.8 28.4 9.7 27 9.7C15.1 9.7 5.5 19.3 5.5 31.2C5.5 43.1 15.1 52.7 27 52.7C38.9 52.7 48.5 43.1 48.5 31.2C48.5 30.9 48.5 30.7 48.5 30.4C49.8 31.1 51.1 31.5 52.5 31.5Z"
            fill="#D99A5B"
            stroke="#3A2A20"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <circle cx="19" cy="23" r="2.5" fill="#3A2A20"/>
          <circle cx="30" cy="35" r="2.5" fill="#3A2A20"/>
          <circle cx="18" cy="39" r="2.2" fill="#3A2A20"/>
          <circle cx="35" cy="17" r="2" fill="#3A2A20"/>
          <circle cx="25" cy="45" r="2" fill="#3A2A20"/>
        </svg>
      </button>
    </Transition>

    <!-- ── Settings Modal ──────────────────────────────── -->
    <Transition name="overlay">
      <div
        v-if="showCustomize"
        class="cb-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cb-modal-title"
        @click.self="closeCustomize"
      >
        <Transition name="sheet">
          <div class="cb-sheet" v-if="showCustomize">
            <div class="cb-sheet-header">
              <div class="cb-sheet-title-group">
                <span class="cb-sheet-icon">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.2" stroke-opacity="0.4"/>
                    <path d="M8 5v4M8 10.5v.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                </span>
                <h2 id="cb-modal-title" class="cb-sheet-title">{{ $t("cookieSettings") }}</h2>
              </div>
              <button class="cb-dismiss cb-dismiss--sheet" aria-label="Close" @click="closeCustomize">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
            </div>

            <p class="cb-sheet-desc">{{ $t("whyWeUseCookies") }}</p>

            <div class="cb-options">
              <!-- Necessary -->
              <div class="cb-option">
                <div class="cb-option-row">
                  <div class="cb-option-info">
                    <span class="cb-option-label cb-option-label--muted">{{ $t("necessaryCookies") }}</span>
                    <span class="cb-tag">{{ $t("alwaysOn") || "Always on" }}</span>
                  </div>
                  <div class="cb-option-controls">
                    <div class="cb-switch cb-switch--on cb-switch--locked" aria-disabled="true"></div>
                    <button
                      class="cb-chevron"
                      :class="{ 'cb-chevron--open': showNecessaryExplanation }"
                      @click="necessaryMore"
                      aria-label="Toggle details"
                    ></button>
                  </div>
                </div>
                <Transition name="expand">
                  <p v-if="showNecessaryExplanation" class="cb-option-desc">
                    {{ $t("necessaryCookiesExplanation") }}
                  </p>
                </Transition>
              </div>

              <!-- Analytics -->
              <div class="cb-option">
                <div class="cb-option-row">
                  <div class="cb-option-info">
                    <span class="cb-option-label">{{ $t("analyticsCookies") }}</span>
                  </div>
                  <div class="cb-option-controls">
                    <button
                      class="cb-switch"
                      :class="{ 'cb-switch--on': draftConsent.analytics }"
                      role="switch"
                      :aria-checked="draftConsent.analytics"
                      @click="draftConsent.analytics = !draftConsent.analytics"
                    ></button>
                    <button
                      class="cb-chevron"
                      :class="{ 'cb-chevron--open': showAnalyticsExplanation }"
                      @click="analyticsMore"
                      aria-label="Toggle details"
                    ></button>
                  </div>
                </div>
                <Transition name="expand">
                  <p v-if="showAnalyticsExplanation" class="cb-option-desc">
                    {{ $t("analyticsCookiesExplanation") }}
                  </p>
                </Transition>
              </div>

              <!-- Marketing -->
              <div class="cb-option">
                <div class="cb-option-row">
                  <div class="cb-option-info">
                    <span class="cb-option-label">{{ $t("marketingCookies") }}</span>
                  </div>
                  <div class="cb-option-controls">
                    <button
                      class="cb-switch"
                      :class="{ 'cb-switch--on': draftConsent.marketing }"
                      role="switch"
                      :aria-checked="draftConsent.marketing"
                      @click="draftConsent.marketing = !draftConsent.marketing"
                    ></button>
                    <button
                      class="cb-chevron"
                      :class="{ 'cb-chevron--open': showMarketingExplanation }"
                      @click="marketsMore"
                      aria-label="Toggle details"
                    ></button>
                  </div>
                </div>
                <Transition name="expand">
                  <p v-if="showMarketingExplanation" class="cb-option-desc">
                    {{ $t("marketingCookiesExplanation") }}
                  </p>
                </Transition>
              </div>
            </div>

            <div class="cb-sheet-footer">
              <button class="cb-btn cb-btn--primary cb-btn--full" @click="saveCustomSettings">
                {{ $t("saveSettings") }}
              </button>
              <button class="cb-btn cb-btn--ghost cb-btn--full" @click="closeCustomize">
                {{ $t("cancel") }}
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </div>
</template>

<script>
export default {
  data() {
    return {
      accepted: true,
      showCustomize: false,
      draftConsent: { analytics: true, marketing: true },
      showNecessaryExplanation: false,
      showAnalyticsExplanation: false,
      showMarketingExplanation: false,
      isReady: false,
      consentGiven: false,
      pendingReject: false,
      // Whether the banner card is showing. Opens automatically while no
      // consent is stored; the cookie circle toggles it.
      bannerOpen: false,
    };
  },

  methods: {
    /**
     * Push Google Consent Mode v2 update to dataLayer.
     * Without this, tags inside GTM stay blocked even after $gtm.enable(),
     * so no analytics/marketing cookies are ever set.
     */
    applyConsent({ analytics, marketing }) {
      if (typeof window === "undefined") return;
      window.dataLayer = window.dataLayer || [];
      // Match the inline bootstrap gtag pattern (pushes arguments object).
      function gtag() {
        window.dataLayer.push(arguments);
      }
      gtag("consent", "update", {
        analytics_storage: analytics ? "granted" : "denied",
        ad_storage: marketing ? "granted" : "denied",
        ad_user_data: marketing ? "granted" : "denied",
        ad_personalization: marketing ? "granted" : "denied",
      });
    },

    persistPrefs({ analytics, marketing }) {
      localStorage.setItem(
        "cookieConsentPrefs",
        JSON.stringify({ analytics: !!analytics, marketing: !!marketing })
      );
    },

    readPrefs() {
      try {
        const raw = localStorage.getItem("cookieConsentPrefs");
        if (!raw) return null;
        const p = JSON.parse(raw);
        return { analytics: !!p.analytics, marketing: !!p.marketing };
      } catch {
        return null;
      }
    },

    acceptAll() {
      this.accepted = true;
      localStorage.setItem("cookiesAccepted", "true");
      localStorage.setItem("cookieInteractionTime", Date.now());
      this.persistPrefs({ analytics: true, marketing: true });
      // Push consent update BEFORE enabling GTM so the script sees granted on load.
      this.applyConsent({ analytics: true, marketing: true });
      this.$gtm.enable();
      this.consentGiven = true;
      this.pendingReject = false;
      this.bannerOpen = false;
      this.updateState();
    },

    rejectAll() {
      this.accepted = true;
      localStorage.setItem("cookiesAccepted", "false");
      localStorage.setItem("cookieInteractionTime", Date.now());
      this.persistPrefs({ analytics: false, marketing: false });
      this.applyConsent({ analytics: false, marketing: false });
      this.consentGiven = true;
      this.pendingReject = true;
      this.bannerOpen = false;
      this.updateState();
    },

    toggleBanner() {
      // Only opens/closes the banner card; the circle icon itself stays
      // put and keeps reflecting the persisted consent state.
      this.bannerOpen = !this.bannerOpen;
    },

    openCustomize() {
      const stored = this.readPrefs();
      this.draftConsent = stored || { analytics: true, marketing: true };
      this.showCustomize = true;
    },

    closeCustomize() {
      this.showCustomize = false;
    },

    saveCustomSettings() {
      this.showCustomize = false;
      this.accepted = true;
      const prefs = {
        analytics: !!this.draftConsent.analytics,
        marketing: !!this.draftConsent.marketing,
      };
      // Push consent update first, then enable GTM if any category granted.
      this.applyConsent(prefs);
      this.persistPrefs(prefs);
      if (prefs.analytics || prefs.marketing) {
        localStorage.setItem("cookiesAccepted", "true");
        this.$gtm.enable();
        this.pendingReject = false;
      } else {
        localStorage.setItem("cookiesAccepted", "false");
        this.pendingReject = true;
      }
      localStorage.setItem("cookieInteractionTime", Date.now());
      this.consentGiven = true;
      this.bannerOpen = false;
      this.updateState();
    },

    updateState() {
      this.$nextTick(() => {
        this.isReady = true;
      });
    },

    necessaryMore() {
      this.showNecessaryExplanation = !this.showNecessaryExplanation;
    },

    analyticsMore() {
      this.showAnalyticsExplanation = !this.showAnalyticsExplanation;
    },

    marketsMore() {
      this.showMarketingExplanation = !this.showMarketingExplanation;
    },

    checkCookiePreference() {
      const preference = localStorage.getItem("cookiesAccepted");
      const lastInteractionTime = localStorage.getItem("cookieInteractionTime");

      if (preference && lastInteractionTime) {
        const timeDifference = Date.now() - lastInteractionTime;

        // Re-ask after 2 months (also covers the 7-day re-ask window)
        if (timeDifference > 4838400000 || timeDifference > 604800000) {
          this.consentGiven = false;
        } else {
          this.consentGiven = true;
          if (preference === "true") {
            // Restore granular consent for this session.
            const prefs = this.readPrefs() || { analytics: true, marketing: true };
            this.applyConsent(prefs);
            this.$gtm.enable();
            this.pendingReject = false;
          } else {
            // User had explicitly rejected — re-push denied so tags stay blocked,
            // and keep the reconsider circle visible until they accept.
            this.applyConsent({ analytics: false, marketing: false });
            this.pendingReject = true;
          }
        }
      } else {
        this.consentGiven = false;
      }

      this.bannerOpen = !this.consentGiven;
      this.isReady = true;
    },
  },

  mounted() {
    this.checkCookiePreference();
  },
};
</script>

<style scoped>
/* ─── Reset scope ──────────────────────────────────── */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* ─── Floating card ────────────────────────────────── */
/* Squarish/vertical, anchored bottom-left just above the WhatsApp button
   (WPWidget.vue: left 1.5%, bottom 2%, 70px icon). */
.cb-card {
  position: fixed;
  left: calc(1.5% + 35px);
  bottom: calc(2% + 110px);
  transform: none;
  z-index: 9999;
  width: min(260px, calc(100vw - 32px));
  background: rgba(11, 18, 32, 0.88);
  backdrop-filter: blur(24px) saturate(160%);
  -webkit-backdrop-filter: blur(24px) saturate(160%);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 18px;
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.03) inset,
    0 8px 32px rgba(0, 0, 0, 0.5),
    0 2px 8px rgba(0, 0, 0, 0.3);
  padding: 20px 18px 18px;
  color: rgba(255, 255, 255, 0.9);
}

.cb-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.cb-text {
  font-size: 13.5px;
  line-height: 1.65;
  color: #ffffff;
  padding-right: 20px;
}

/* ─── Banner actions ───────────────────────────────── */
.cb-actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}

.cb-actions .cb-btn {
  width: 100%;
  text-align: center;
  justify-content: center;
}

/* ─── Button system ────────────────────────────────── */
.cb-btn {
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.01em;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  padding: 8px 16px;
  line-height: 1;
  white-space: nowrap;
  transition:
    background 0.18s ease,
    transform 0.15s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.18s ease,
    opacity 0.15s ease;
}

.cb-btn:active {
  transform: translateY(1px) !important;
}

.cb-btn--primary {
  background: rgba(255, 255, 255, 0.96);
  color: #0b1220;
}

.cb-btn--primary:hover {
  background: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(255, 255, 255, 0.12);
}

.cb-btn--ghost {
  background: transparent;
  color: #ffffff;
  border: none;
  text-decoration: underline;
  text-decoration-color: rgba(255, 255, 255, 0.35);
  text-underline-offset: 3px;
}

.cb-btn--ghost:hover {
  color: #ffffff;
  text-decoration-color: rgba(255, 255, 255, 0.65);
}

.cb-btn--text {
  background: rgba(255, 255, 255, 0.06);
  color: #ffffff;
  padding: 8px 14px;
  text-decoration: none;
}

.cb-btn--text:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  transform: translateY(-1px);
}

.cb-btn--full {
  width: 100%;
  text-align: center;
  justify-content: center;
  padding: 10px 16px;
}

/* ─── Dismiss button ───────────────────────────────── */
.cb-dismiss {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.25);
  border-radius: 6px;
  transition: color 0.15s ease, background 0.15s ease;
}

.cb-dismiss:hover {
  color: rgba(255, 255, 255, 0.7);
  background: rgba(255, 255, 255, 0.06);
}

.cb-dismiss--sheet {
  position: static;
  flex-shrink: 0;
}

/* ─── Modal overlay ────────────────────────────────── */
.cb-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(4, 8, 18, 0.7);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

/* ─── Settings sheet ───────────────────────────────── */
.cb-sheet {
  width: min(460px, 100%);
  max-height: min(620px, 90dvh);
  background: #0c1525;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 20px;
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.03) inset,
    0 32px 80px rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: rgba(255, 255, 255, 0.88);
}

.cb-sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 22px 0;
}

.cb-sheet-title-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cb-sheet-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.07);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.4);
  flex-shrink: 0;
}

.cb-sheet-title {
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: rgba(255, 255, 255, 0.92);
}

.cb-sheet-desc {
  font-size: 13px;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.38);
  padding: 10px 22px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

/* ─── Options ──────────────────────────────────────── */
.cb-options {
  flex: 1;
  overflow-y: auto;
  padding: 6px 0;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.08) transparent;
}

.cb-option {
  padding: 14px 22px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.cb-option:last-child {
  border-bottom: none;
}

.cb-option-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.cb-option-info {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.cb-option-label {
  font-size: 13.5px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.82);
  white-space: nowrap;
}

.cb-option-label--muted {
  color: rgba(255, 255, 255, 0.4);
}

.cb-option-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.cb-option-desc {
  margin-top: 10px;
  font-size: 12px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.35);
}

.cb-tag {
  font-size: 11px;
  font-weight: 500;
  color: rgba(110, 231, 183, 0.85);
  background: rgba(110, 231, 183, 0.08);
  border: 1px solid rgba(110, 231, 183, 0.14);
  border-radius: 99px;
  padding: 2px 8px;
  white-space: nowrap;
}

/* ─── Toggle switch ────────────────────────────────── */
.cb-switch {
  width: 38px;
  height: 21px;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  padding: 0;
  position: relative;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.22s cubic-bezier(0.22, 1, 0.36, 1);
}

.cb-switch::after {
  content: "";
  position: absolute;
  top: 2.5px;
  left: 2.5px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  transition:
    transform 0.22s cubic-bezier(0.22, 1, 0.36, 1),
    background 0.22s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
}

.cb-switch--on {
  background: rgba(255, 255, 255, 0.92);
}

.cb-switch--on::after {
  transform: translateX(17px);
  background: #0b1220;
}

.cb-switch--locked {
  cursor: not-allowed;
  opacity: 0.45;
}

/* ─── Chevron ──────────────────────────────────────── */
.cb-chevron {
  width: 20px;
  height: 20px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.25);
  transition: color 0.15s ease, transform 0.2s ease;
  flex-shrink: 0;
  padding: 0;
}

.cb-chevron::after {
  content: "";
  width: 6px;
  height: 6px;
  border-right: 1.5px solid currentColor;
  border-bottom: 1.5px solid currentColor;
  transform: rotate(45deg) translateY(-2px);
  display: block;
  transition: transform 0.2s ease;
}

.cb-chevron--open {
  color: rgba(255, 255, 255, 0.5);
}

.cb-chevron--open::after {
  transform: rotate(225deg) translateY(-2px);
}

.cb-chevron:hover {
  color: rgba(255, 255, 255, 0.6);
}

/* ─── Sheet footer ─────────────────────────────────── */
.cb-sheet-footer {
  padding: 16px 22px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

/* ─── Card entrance ────────────────────────────────── */
.card-enter-active {
  transition:
    opacity 0.32s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.36s cubic-bezier(0.22, 1, 0.36, 1);
}

.card-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.2s ease;
}

.card-enter-from {
  opacity: 0;
  transform: translateY(40px);
}

.card-leave-to {
  opacity: 0;
  transform: translateY(16px);
}

/* ─── Reconsider circle ─────────────────────────────── */
.cb-circle {
  position: fixed;
  left: calc(1.5% + 10px);
  bottom: calc(2% + 70px);
  z-index: 9999;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  color: #e7bb7f;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.25);
  transition: background 0.15s ease;
  animation: cb-circle-shake 4s ease-in-out infinite;
}

@keyframes cb-circle-shake {
  0%, 90%, 100% { transform: rotate(0deg); }
  91% { transform: rotate(-10deg); }
  92% { transform: rotate(9deg); }
  93% { transform: rotate(-7deg); }
  94% { transform: rotate(7deg); }
  95% { transform: rotate(-4deg); }
  96% { transform: rotate(4deg); }
  97% { transform: rotate(-1deg); }
  98% { transform: rotate(1deg); }
}

.cb-circle:hover {
  background: #f5f0e8;
  transform: translateY(-1px);
}

.cb-circle svg {
  position: relative;
  left: 2px;
}

.circle-enter-active,
.circle-leave-active {
  transition: opacity 0.22s ease, transform 0.22s cubic-bezier(0.22, 1, 0.36, 1);
}

.circle-enter-from,
.circle-leave-to {
  opacity: 0;
  transform: scale(0.7);
}

/* ─── Overlay fade ─────────────────────────────────── */
.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.22s ease;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

/* ─── Sheet entrance ───────────────────────────────── */
.sheet-enter-active {
  transition:
    opacity 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
}

.sheet-leave-active {
  transition:
    opacity 0.16s ease,
    transform 0.18s ease;
}

.sheet-enter-from {
  opacity: 0;
  transform: scale(0.96) translateY(8px);
}

.sheet-leave-to {
  opacity: 0;
  transform: scale(0.98);
}

/* ─── Expand ───────────────────────────────────────── */
.expand-enter-active,
.expand-leave-active {
  transition:
    opacity 0.2s ease,
    max-height 0.24s cubic-bezier(0.22, 1, 0.36, 1);
  overflow: hidden;
  max-height: 160px;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
}

/* ─── Mobile ───────────────────────────────────────── */
@media (max-width: 600px) {
  .cb-card {
    left: 0;
    right: 0;
    bottom: 0;
    transform: none;
    width: 100%;
    border-radius: 18px 18px 0 0;
    padding-top: 30px;
    padding-bottom: max(35px, env(safe-area-inset-bottom));
    z-index: 2147483647; /* max 32-bit int — covers everything */
  }

  .cb-text {
    font-size: 15px;
  }

  /* Bottom sheet: slides up from below the screen edge, back down on close. */
  .card-enter-active {
    transition: transform 0.85s cubic-bezier(0.22, 1, 0.36, 1);
  }

  .card-leave-active {
    transition: transform 0.45s cubic-bezier(0.55, 0, 0.75, 0.2);
  }

  .card-enter-from,
  .card-leave-to {
    opacity: 1;
    transform: translateY(100%);
  }

  .cb-circle {
    left: auto;
    right: 24px;
    bottom: calc(1% + 145px);
    width: 64px;
    height: 64px;
  }

  .cb-circle svg {
    width: 38px;
    height: 38px;
  }

  .cb-overlay {
    z-index: 2147483647;
  }

  .cb-actions {
    flex-direction: row;
    gap: 6px;
  }

  .cb-actions .cb-btn {
    flex: 1;
    padding: 10px 4px;
    font-size: 13px;
  }

  .cb-btn--text { order: 1; }
  .cb-btn--ghost { order: 2; }
  .cb-btn--primary { order: 3; }

  .cb-sheet {
    max-height: 88dvh;
    border-radius: 20px 20px 0 0;
    margin-top: auto;
  }

  .cb-overlay {
    align-items: flex-end;
    padding: 0;
  }
}
</style>
